import { useEffect, useRef, useState } from "react";
import { Save, Eye, EyeOff, Image as ImageIcon, Link as LinkIcon, Clock, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";
import OfferCountdown from "@/components/OfferCountdown";

const DEFAULT = { enabled: false, image_url: "", headline: "", subline: "", cta_label: "", cta_link: "", expires_at: "", countdown_style: "inline" };

// Convert an ISO string ("2025-12-31T18:30:00Z" or "…+00:00") to the value
// expected by <input type="datetime-local"> ("YYYY-MM-DDTHH:mm") in LOCAL time.
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Convert a datetime-local value back to an ISO string (UTC).
const localInputToIso = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
};

export default function OfferPopupSettings() {
  usePageTitle("Offer Popup");
  const [form, setForm] = useState(DEFAULT);
  const [expiryLocal, setExpiryLocal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/offer-popup").then((r) => {
      const merged = { ...DEFAULT, ...r.data };
      setForm(merged);
      setExpiryLocal(isoToLocalInput(merged.expires_at));
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/media?category=offer_popup&label=${encodeURIComponent(file.name)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      // /media returns { id, category, ... } — reconstruct absolute URL for the popup
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/media/${data.id}`;
      set("image_url", url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, expires_at: localInputToIso(expiryLocal) };
      await api.put("/admin/offer-popup", payload);
      setForm(payload);
      toast.success(`Popup ${form.enabled ? "enabled" : "saved (disabled)"}`);
      // Clear today's dismissal flag so admin sees updated popup immediately
      Object.keys(localStorage).filter((k) => k.startsWith("offer_popup_shown_")).forEach((k) => localStorage.removeItem(k));
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="offer-popup-admin">
      <div className="flex items-end justify-between border-b border-[hsl(var(--border))] pb-6">
        <div>
          <div className="overline mb-2">Marketing</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Offer Popup</h1>
          <p className="text-sm text-zinc-500 mt-1">Shown once per day to every visitor. Add an image, headline and a call-to-action.</p>
        </div>
        <button onClick={save} disabled={saving || loading} className="btn-swiss btn-accent" data-testid="offer-popup-save">
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="card-swiss p-5 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border-2" style={{ borderColor: form.enabled ? "#10b981" : "#e5e7eb", background: form.enabled ? "#ecfdf5" : "#fff" }}>
            <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="offer-popup-enabled" className="w-5 h-5" />
            <div className="flex-1">
              <div className="font-bold text-sm">Show popup to visitors</div>
              <div className="text-xs text-zinc-500">{form.enabled ? "Active — visitors will see it once per day" : "Disabled — popup is hidden site-wide"}</div>
            </div>
            {form.enabled ? <Eye size={16} className="text-emerald-600" /> : <EyeOff size={16} className="text-zinc-400" />}
          </label>

          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1 inline-flex items-center gap-1"><ImageIcon size={11} /> Popup Image</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => uploadImage(e.target.files?.[0])}
              className="hidden"
              data-testid="popup-image-file"
            />
            <div className="flex gap-2">
              <input
                value={form.image_url}
                onChange={(e) => set("image_url", e.target.value)}
                placeholder="Upload or paste https://…jpg (recommended 800×600)"
                className="flex-1 border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none font-mono"
                data-testid="popup-image-url"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 border-2 border-zinc-200 hover:border-[#FF6B00] hover:text-[#FF6B00] rounded-xl px-3 py-2 text-xs font-bold disabled:opacity-50"
                data-testid="popup-image-upload-btn"
              >
                <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
              </button>
              {form.image_url && (
                <button
                  type="button"
                  onClick={() => set("image_url", "")}
                  className="p-2 text-zinc-400 hover:text-red-600"
                  title="Remove image"
                  data-testid="popup-image-clear-btn"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <p className="mt-1 text-[10px] text-zinc-500">JPG/PNG/WebP up to 5MB · stored on Emergent Object Storage.</p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">Headline</label>
            <input value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder="Flat 20% off Business Cards!" className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm font-bold outline-none" data-testid="popup-headline" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">Subline</label>
            <textarea value={form.subline} onChange={(e) => set("subline", e.target.value)} rows={2} placeholder="Order today — free doorstep delivery on orders above ₹999." className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none" data-testid="popup-subline" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">CTA Label</label>
              <input value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} placeholder="Shop Now" className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none" data-testid="popup-cta-label" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1 inline-flex items-center gap-1"><LinkIcon size={10} /> CTA Link</label>
              <input value={form.cta_link} onChange={(e) => set("cta_link", e.target.value)} placeholder="/products/business-cards" className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none font-mono" data-testid="popup-cta-link" />
            </div>
          </div>

          {/* Countdown timer (optional) */}
          <div className="border-t border-dashed border-zinc-200 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wider font-bold text-zinc-500 inline-flex items-center gap-1">
                <Clock size={11} /> Countdown Timer <span className="normal-case tracking-normal text-zinc-400 font-normal">(optional)</span>
              </label>
              {expiryLocal && (
                <button type="button" onClick={() => setExpiryLocal("")} className="text-[11px] text-red-600 hover:underline font-bold" data-testid="popup-expiry-clear">
                  Clear timer
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1">Offer ends at</label>
                <input
                  type="datetime-local"
                  value={expiryLocal}
                  onChange={(e) => setExpiryLocal(e.target.value)}
                  className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none font-mono"
                  data-testid="popup-expires-at"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1">Display style</label>
                <div className="flex gap-2">
                  {[
                    { v: "inline", l: "Inline pill" },
                    { v: "blocks", l: "Bold blocks" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => set("countdown_style", opt.v)}
                      className={`flex-1 rounded-xl border-2 px-3 py-2 text-xs font-bold transition ${form.countdown_style === opt.v ? "border-[#FF6B00] bg-orange-50 text-[#FF6B00]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}
                      data-testid={`popup-countdown-style-${opt.v}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500">Leave empty to hide the timer. When the timer hits zero the popup auto-hides for all visitors.</p>
          </div>

          <div className="text-[11px] text-zinc-500 bg-amber-50 border border-amber-200 rounded-xl p-3">
            💡 The popup appears once per day per visitor (tracked via `localStorage`). Saving with new content clears your own dismissal cookie so you can preview immediately.
          </div>
        </div>

        {/* Live preview */}
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">Live Preview</div>
          <div className="bg-zinc-900 rounded-2xl p-8 flex items-center justify-center min-h-[400px]" data-testid="popup-preview-frame">
            <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
              {form.image_url && (
                <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
                  <img src={form.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              )}
              <div className="p-5 text-center">
                {form.headline ? (
                  <div className="font-display text-2xl font-black tracking-tighter">{form.headline}</div>
                ) : (
                  <div className="text-zinc-300 italic text-sm">Add a headline…</div>
                )}
                {form.subline && <p className="mt-2 text-sm text-zinc-600">{form.subline}</p>}
                {expiryLocal && (
                  <OfferCountdown
                    expiresAt={localInputToIso(expiryLocal)}
                    style={form.countdown_style || "inline"}
                  />
                )}
                {form.cta_label && (
                  <div className="mt-4 inline-block bg-[#FF6B00] text-white font-bold px-6 py-2 rounded-full text-sm">
                    {form.cta_label} →
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
